import React, { useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Collapse,
  IconButton,
  VStack,
  Badge,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  ExamAttemptScoreStatusByTutor,
  ExamAttemptScoreStatus,
  ExamAttemptedQuestionSetScoreStatus,
} from "../Common/types";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";

interface ExamAttemptScoringStatusProps {
  examAttemptScoringStatuses: ExamAttemptScoreStatusByTutor[];
  onOverride: (examAttemptId: number) => void;
  onViewAttempt: (examAttemptId: number) => void;
}

const ExamAttemptScoringStatus: React.FC<ExamAttemptScoringStatusProps> = ({
  examAttemptScoringStatuses,
  onOverride,
  onViewAttempt
}) => {
  const [expandedAttempts, setExpandedAttempts] = useState<{ [key: number]: boolean }>({});

  const toggleExpand = (attemptId: number) => {
    setExpandedAttempts(prev => ({
      ...prev,
      [attemptId]: !prev[attemptId],
    }));
  };

return (
  <Box mx="auto" overflowY="auto">
    <Accordion allowMultiple defaultIndex={[0]}>
      {examAttemptScoringStatuses.map((group) => (
        <AccordionItem key={group.tutor_id} borderWidth="1px" borderRadius="md" mb={4}>
          <h2>
            <AccordionButton _expanded={{ bg: "blue.50" }}>
              <Box as="span" flex="1" textAlign="left">
                <Heading size="sm">
                  Tutor: {group.tutor_name} (ID: {group.tutor_id})
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
          </h2>

          <AccordionPanel pb={4}>
            <Table variant="simple" size="sm">
              <Thead bg="gray.100">
                <Tr>
                  <Th>Student Name</Th>
                  <Th>Manual Scoring</Th>
                  <Th>Auto Scoring</Th>
                  <Th>Qset Status</Th>
                  <Th>View Attempt Score</Th>
                  <Th>Override Evaluation</Th>
                </Tr>
              </Thead>
              <Tbody>
                {group.exam_attempts_score_status.map((attempt) => (
                  <React.Fragment key={attempt.exam_attempt_id}>
                    <Tr>
                      <Td>{attempt.student_name}</Td>
                      <Td>
                        <Badge colorScheme={attempt.manual_scoring_completed ? "green" : "red"}>
                          {attempt.manual_scoring_completed ? "Completed" : "Pending"}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={attempt.auto_scoring_completed ? "green" : "red"}>
                          {attempt.auto_scoring_completed ? "Completed" : "Pending"}
                        </Badge>
                      </Td>
                      <Td>
                        <IconButton
                          aria-label="Toggle details"
                          icon={expandedAttempts[attempt.exam_attempt_id] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          onClick={() => toggleExpand(attempt.exam_attempt_id)}
                          size="sm"
                          variant="ghost"
                        />
                      </Td>
                      <Td>
                          <Button
                            onClick={() => onViewAttempt(attempt.exam_attempt_id)}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            isDisabled={!(attempt.auto_scoring_completed && attempt.manual_scoring_completed)}
                          >
                            View Exam Attempt
                          </Button>
                          </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          {attempt.is_evaluation_overridden && (
                            <Badge colorScheme="purple">Overridden</Badge>
                          )}
                          {attempt.is_manual_score_revised && (
                            <Badge colorScheme="orange">Revised</Badge>
                          )}
                          {attempt.evaluated_by_username && (
                            <Text fontSize="xs" color="gray.600">
                              Evaluated by: <strong>{attempt.evaluated_by_username}</strong>
                            </Text>
                          )}
                          {attempt.last_evaluated_on && (
                          <Text fontSize="xs" color="gray.500">
                            Last Evaluated:{" "}
                            <strong>
                              {attempt.last_evaluated_on
                                ? fmtLocal(attempt.last_evaluated_on, {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                    weekday: undefined,
                                    second: undefined,
                                    timeZoneName: undefined,
                                  })?.replace(",", " at")
                                : "Not available"}
                            </strong>
                          </Text>
                          )}
                          <Button
                            onClick={() => onOverride(attempt.exam_attempt_id)}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                          >
                            Override Manual Scoring
                          </Button>
                        </VStack>
                      </Td>

                    </Tr>

                    <Tr>
                      <Td colSpan={6} p={0}>
                        <Collapse in={expandedAttempts[attempt.exam_attempt_id]} animateOpacity>
                          <Box bg="gray.50" p={3} borderTopWidth="1px">
                            <Table variant="striped" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Question Set</Th>
                                  <Th>Manual Scoring</Th>
                                  <Th>Auto Scoring</Th>
                            
                                </Tr>
                              </Thead>
                              <Tbody>
                                {attempt.question_set_score_status.map((qset) => (
                                  <Tr key={qset.exam_question_set_attempt_id}>
                                    <Td>{qset.question_set_title}</Td>
                                    <Td>
                                      <Badge colorScheme={qset.manual_scoring_completed ? "green" : "red"}>
                                        {qset.manual_scoring_completed ? "Completed" : "Pending"}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme={qset.auto_scoring_completed ? "green" : "red"}>
                                        {qset.auto_scoring_completed ? "Completed" : "Pending"}
                                      </Badge>
                                    </Td>
              
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>
                        </Collapse>
                      </Td>
                    </Tr>
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  </Box>
);

};

export default ExamAttemptScoringStatus;
